import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, BadgeCheck, Building2, ClipboardCheck, FileCheck2, HandCoins,
  Lightbulb, PanelTop, Phone, PlugZap, ShieldCheck, SunMedium, Wrench,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { IMAGES, PHONE_DISPLAY, PHONE_TEL, SINCE, SERVED_AREAS } from "@/constants/site";

const MARQUEE_ITEMS = [
  "PM Surya Ghar Subsidy Support",
  "Serving Nandurbar & Shahada Since 2017",
  "25-Year Panel Performance Warranty",
  "MSEDCL Net Metering Handled",
  "Solar Street Lights & Control Panels",
  "Free Site Survey",
];

const BENTO = [
  {
    id: "residential",
    icon: PanelTop,
    title: "On-Grid Rooftop Solar",
    desc: "Grid-connected systems for homes, shops and offices from 3 kW to 10 kW+ — with subsidy and net metering handled end-to-end.",
    cls: "md:col-span-2 lg:col-span-2 lg:row-span-2 bg-gradient-to-br from-amber-50 to-orange-50 border border-orange-200/60",
    iconCls: "bg-orange-600 text-white",
    link: true,
  },
  {
    id: "commercial",
    icon: Building2,
    title: "Commercial & Industrial",
    desc: "10 kW to 100 kW+ plants that cut heavy day-time power bills for factories, schools, hospitals and godowns.",
    cls: "md:col-span-2 lg:col-span-1 lg:row-span-2 bg-slate-950 text-white border border-slate-950",
    iconCls: "bg-amber-400 text-slate-950",
    link: true,
  },
  {
    id: "street-lights",
    icon: Lightbulb,
    title: "Solar Street Lights",
    desc: "Supply and installation for farms, societies and gram panchayats.",
    cls: "bg-white border border-slate-200",
    iconCls: "bg-amber-100 text-orange-600",
  },
  {
    id: "offgrid",
    icon: PlugZap,
    title: "Off-Grid & Hybrid",
    desc: "Battery-backed systems that keep you powered through outages.",
    cls: "bg-white border border-slate-200",
    iconCls: "bg-amber-100 text-orange-600",
  },
  {
    id: "maintenance",
    icon: Wrench,
    title: "Maintenance, Cleaning & Health Audits",
    desc: "Panel cleaning, generation checks, fault repair and system upgrades — even for systems we did not install.",
    cls: "md:col-span-2 lg:col-span-2 bg-white border border-slate-200",
    iconCls: "bg-amber-100 text-orange-600",
  },
  {
    id: "subsidy",
    icon: HandCoins,
    title: "Subsidy & Net-Metering Support",
    desc: "We prepare, file and follow your PM Surya Ghar subsidy application and MSEDCL net-metering paperwork from start to approval.",
    cls: "md:col-span-2 lg:col-span-2 bg-white border border-slate-200",
    iconCls: "bg-amber-100 text-orange-600",
  },
];

const PROCESS = [
  { icon: ClipboardCheck, title: "Free Site Survey", desc: "Shadow analysis and load study at your roof." },
  { icon: FileCheck2, title: "Proposal & Quotation", desc: "Custom design with transparent, itemised pricing." },
  { icon: HandCoins, title: "Subsidy & Approval", desc: "We file your subsidy application and DISCOM paperwork." },
  { icon: SunMedium, title: "Installation", desc: "Professional mounting and wiring in 2–3 working days." },
  { icon: BadgeCheck, title: "Net Metering & Switch-On", desc: "Meter changeover, testing and handover." },
];

const FAQS = [
  {
    q: "How much subsidy can I get under PM Surya Ghar Muft Bijli Yojana?",
    a: "Residential consumers can receive ₹30,000 per kW for systems up to 2 kW, ₹60,000 for a 2 kW system, and up to ₹78,000 for 3 kW or larger systems. We prepare and file the complete subsidy application for you and follow it through approval.",
  },
  {
    q: "How long does installation take?",
    a: "A typical residential rooftop installation takes 2–3 working days after equipment delivery. Net-metering approval and subsidy disbursement usually complete within a few weeks — we coordinate with the electricity board throughout.",
  },
  {
    q: "What is net metering?",
    a: "Net metering lets you export unused solar power to the grid. Your meter records the units you send back, and you pay only for the net units you consume — effectively shrinking your bill dramatically.",
  },
  {
    q: "What warranties do you provide?",
    a: "Solar panels carry a 25–30 year performance warranty from the manufacturer, inverters typically carry 5 years, and we stay available for cleaning, servicing and health audits after installation.",
  },
];

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: 0 },
  transition: { delay: 0.15 + i * 0.12, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
});

const Calculator = () => {
  const [bill, setBill] = useState(2500);
  const kw = Math.max(1, Math.min(10, Math.round((bill / 1100) * 2) / 2));
  const subsidy = bill <= 0 ? 0 : kw <= 2 ? Math.round(kw) * 30000 : 78000;
  const annual = Math.round(bill * 12 * 0.9);
  const fmt = (n) => n.toLocaleString("en-IN");

  return (
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <Reveal>
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-600">Savings Calculator</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          How much could you save?
        </h2>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Move the slider to your average monthly electricity bill and see the solar
          system size, government subsidy and yearly savings you can expect.
        </p>
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <label htmlFor="bill-range" className="text-sm font-semibold text-slate-700">
              Monthly electricity bill
            </label>
            <span data-testid="calc-bill-value" className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-1 font-mono text-sm font-semibold text-orange-700">
              ₹{fmt(bill)}
            </span>
          </div>
          <input
            id="bill-range"
            type="range"
            min="500"
            max="20000"
            step="100"
            value={bill}
            onChange={(e) => setBill(Number(e.target.value))}
            data-testid="calc-bill-input"
            className="mt-4 w-full accent-orange-600"
          />
          <div className="mt-1 flex justify-between text-xs text-slate-400 font-medium">
            <span>₹500</span>
            <span>₹20,000</span>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-slate-950 text-white p-6 sm:col-span-1" data-testid="calc-kw-card">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-400">Recommended size</p>
            <p data-testid="calc-kw-value" className="mt-3 font-display text-4xl font-extrabold">{kw} kW</p>
            <p className="mt-2 text-xs text-slate-400">rooftop solar system</p>
          </div>
          <div className="rounded-2xl border border-orange-200 bg-orange-50 p-6" data-testid="calc-subsidy-card">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-700">PM Surya Ghar subsidy</p>
            <p data-testid="calc-subsidy-value" className="mt-3 font-display text-4xl font-extrabold text-orange-600">₹{fmt(subsidy)}</p>
            <p className="mt-2 text-xs text-orange-700/70">up to ₹78,000 for 3 kW+</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="calc-savings-card">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Annual bill savings</p>
            <p data-testid="calc-savings-value" className="mt-3 font-display text-4xl font-extrabold text-slate-900">₹{fmt(annual)}</p>
            <p className="mt-2 text-xs text-slate-400">approx. per year</p>
          </div>
          <Link
            to="/contact"
            data-testid="calc-quote-button"
            className="sm:col-span-3 inline-flex items-center justify-center gap-2 rounded-full bg-orange-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-orange-600/25 hover:bg-orange-700 transition-colors"
          >
            Get this system sized for free <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
        <p className="mt-4 text-xs text-slate-400">
          Estimates only — actual figures depend on your roof, usage pattern and DISCOM tariff.
        </p>
      </Reveal>
    </div>
  );
};

const Home = () => {
  const { scrollYProgress } = useScroll();
  const yImg = useTransform(scrollYProgress, [0, 0.2], [0, 80]);
  const scaleImg = useTransform(scrollYProgress, [0, 0.2], [1, 1.08]);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-white to-amber-50/70">
        <div className="pointer-events-none absolute -top-40 -right-40 h-[560px] w-[560px] rounded-full border border-orange-200/50" />
        <div className="pointer-events-none absolute -top-24 -right-24 h-[380px] w-[380px] rounded-full border border-orange-200/40" />
        <div className="pointer-events-none absolute top-1/3 right-[-120px] h-[420px] w-[420px] rounded-full bg-amber-200/30 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-amber-600"
              data-testid="hero-eyebrow"
            >
              Rooftop Solar Installation · Nandurbar, Maharashtra
            </motion.p>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.04] text-slate-950">
              {["Your roof,", "turned into a"].map((t, i) => (
                <span key={t} className="block overflow-hidden pb-1">
                  <motion.span className="block" {...lineAnim(i)}>{t}</motion.span>
                </span>
              ))}
              <span className="block overflow-hidden pb-2">
                <motion.span className="block bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500 bg-clip-text text-transparent" {...lineAnim(2)}>
                  power plant.
                </motion.span>
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-md"
              data-testid="hero-subline"
            >
              Swarajya Solar Services designs, installs and maintains rooftop solar
              systems for homes, businesses and industries — with complete PM Surya
              Ghar subsidy and MSEDCL net-metering support.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.7 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link
                to="/contact"
                data-testid="hero-quote-button"
                className="inline-flex items-center gap-2 rounded-full bg-orange-600 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-600/25 hover:bg-orange-700 transition-colors"
              >
                Get Free Quote <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href={PHONE_TEL}
                data-testid="hero-call-button"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-base font-semibold text-slate-800 hover:border-orange-400 hover:text-orange-600 transition-colors"
              >
                <Phone className="h-5 w-5 text-orange-600" /> Call {PHONE_DISPLAY}
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.85, duration: 0.8 }}
              className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
              data-testid="hero-trust-chips"
            >
              {[
                { icon: SunMedium, label: `Trusted since ${SINCE}` },
                { icon: ShieldCheck, label: "25-Year Panel Warranty" },
                { icon: BadgeCheck, label: "Subsidy Paperwork Done For You" },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Icon className="h-4.5 w-4.5 h-5 w-5 text-orange-600" /> {label}
                </span>
              ))}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div
              style={{ y: yImg }}
              className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-orange-900/10 ring-1 ring-slate-900/10"
              data-testid="hero-image"
            >
              <motion.img
                style={{ scale: scaleImg }}
                src={IMAGES.hero}
                alt="Rooftop solar panel installation on a home"
                className="h-[380px] sm:h-[440px] lg:h-[560px] w-full object-cover object-bottom"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.7 }}
              className="absolute -bottom-6 left-4 sm:left-8 flex items-center gap-3 rounded-2xl bg-white/95 backdrop-blur px-5 py-4 shadow-xl ring-1 ring-slate-900/10"
              data-testid="hero-subsidy-card"
            >
              <div className="rounded-xl bg-amber-100 p-2.5">
                <BadgeCheck className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  PM Surya Ghar Subsidy
                </p>
                <p className="font-display text-xl font-bold text-slate-900">Save up to ₹78,000</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={MARQUEE_ITEMS} />

      {/* SERVICES BENTO */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-amber-600">What We Do</p>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950">
                  Solar for every kind of roof
                </h2>
              </div>
              <Link to="/services" data-testid="bento-all-services-link" className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors">
                Explore all services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:auto-rows-[236px]">
            {BENTO.map(({ id, icon: Icon, title, desc, cls, iconCls, link }, i) => (
              <Reveal key={id} delay={i * 0.06} className={`${cls} rounded-3xl p-7 flex flex-col justify-between overflow-hidden relative`}>
                <div>
                  <div className={`inline-flex rounded-2xl p-3 ${iconCls}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{desc}</p>
                </div>
                {link && (
                  <Link
                    to="/services"
                    data-testid={`bento-link-${id}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:gap-2.5 transition-all"
                  >
                    Learn more <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-amber-50/60 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Calculator />
        </div>
      </section>

      {/* SHOWCASE / PROCESS SPOTLIGHT */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="pointer-events-none absolute -inset-6 rounded-[2.5rem] bg-amber-200/30 blur-2xl" />
            <div className="relative overflow-hidden rounded-[2rem] ring-1 ring-slate-900/10 shadow-2xl shadow-slate-900/10">
              <img
                src={IMAGES.engineer}
                alt="Solar engineer installing panels on a rooftop"
                loading="lazy"
                className="h-[340px] sm:h-[440px] w-full object-cover"
                data-testid="showcase-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex flex-wrap gap-3">
                {[`Since ${SINCE}`, "2–3 day installs", "25-yr panel warranty"].map((t) => (
                  <span key={t} className="rounded-full bg-white/90 backdrop-blur px-4 py-1.5 text-xs font-bold text-slate-800">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal>
              <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-amber-600">From Survey To Switch-On</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950">
                One team handles everything
              </h2>
            </Reveal>
            <div className="mt-8 space-y-5">
              {PROCESS.map(({ icon: Icon, title, desc }, i) => (
                <Reveal key={title} delay={i * 0.07}>
                  <div className="flex gap-4" data-testid={`process-step-${i + 1}`}>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-50 border border-orange-200/70 font-mono text-sm font-semibold text-orange-600">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
                        {title} <Icon className="h-4 w-4 text-orange-500" />
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY SWARAJYA */}
      <section className="py-20 lg:py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-amber-500">Why Swarajya</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-2xl">
              A local team you can actually reach
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: SunMedium,
                title: `Rooted here since ${SINCE}`,
                desc: `Serving Nandurbar, Shahada and the surrounding region as a local solar and electrical business — we are here before the sale and long after it too.`,
              },
              {
                icon: BadgeCheck,
                title: "Honest, wholesale-backed pricing",
                desc: "As suppliers of solar panels, control panels and electrical equipment, we install at direct-from-market rates with no middlemen.",
              },
              {
                icon: ShieldCheck,
                title: "Paperwork handled, fully",
                desc: "Subsidy application, net metering, DISCOM coordination — one team takes your system from first survey to final switch-on.",
              },
            ].map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={i * 0.08} className="rounded-3xl border border-slate-800 bg-slate-900/60 p-8 flex flex-col gap-4">
                <div className="inline-flex w-fit rounded-2xl bg-amber-400/10 p-3">
                  <Icon className="h-6 w-6 text-amber-400" />
                </div>
                <h3 className="font-display text-xl font-bold text-white">{title}</h3>
                <p className="text-sm leading-relaxed text-slate-400">{desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-amber-600 text-center">FAQ</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950 text-center">
              Solar questions, answered
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-slate-200">
                  <AccordionTrigger data-testid={`faq-trigger-${i + 1}`} className="text-left font-display text-base sm:text-lg font-semibold text-slate-900 hover:no-underline hover:text-orange-600 transition-colors py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-slate-600 pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 lg:pb-28 px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-[2rem] bg-slate-950 px-6 py-16 sm:px-12 sm:py-20 text-center">
            <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[720px] rounded-full bg-orange-600/20 blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
                Ready to cut your electricity bill?
              </h2>
              <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-xl mx-auto">
                Book a free site survey in {SERVED_AREAS}. We respond the same day.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-3">
                <a
                  href={PHONE_TEL}
                  data-testid="cta-call-button"
                  className="inline-flex items-center gap-2 rounded-full bg-orange-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-orange-600/30 hover:bg-orange-700 transition-colors"
                >
                  <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
                </a>
                <Link
                  to="/contact"
                  data-testid="cta-quote-button"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-8 py-4 text-base font-semibold text-white hover:border-orange-500 transition-colors"
                >
                  Request Free Quote <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
